/**
 * Memoized Redux selectors for the `liveMode` slice.
 *
 * Several arrays in the `liveMode` reducer (participants, tutors, in-call users,
 * raised hands) are recreated with a fresh reference on every relevant action
 * (joinCall, raiseHand, lowerHand, getCall…). Connected components that read
 * these slices through `mapStateToProps` therefore re-render even when the
 * underlying content is identical.
 *
 * These selectors wrap the raw slices with `reselect.createSelectorCreator` +
 * `shallowEqual` so the cached reference is returned whenever the new array is
 * shallow-equal to the previous one (arrays of primitive IDs → shallowEqual
 * gives content-equality). This keeps `connect` stable and prevents the
 * cascade of re-renders identified in the live-mode perf audit (epicenter:
 * `LKVideoCallPortal.web.js`).
 */
import { createSelectorCreator, defaultMemoize } from "reselect"
import { shallowEqual } from "react-redux"

const createShallowEqualSelector = createSelectorCreator(
    defaultMemoize,
    shallowEqual
)

const EMPTY_ARRAY = Object.freeze([])

const selectLiveModeState = (state) => state.liveMode

export const selectLiveParticipants = createShallowEqualSelector(
    (state) => selectLiveModeState(state)?.liveParticipants ?? EMPTY_ARRAY,
    (participants) => participants
)

export const selectLiveTutors = createShallowEqualSelector(
    (state) => selectLiveModeState(state)?.liveTutors ?? EMPTY_ARRAY,
    (tutors) => tutors
)

export const selectInCallUsers = createShallowEqualSelector(
    (state) => selectLiveModeState(state)?.inCallUsers ?? EMPTY_ARRAY,
    (inCallUsers) => inCallUsers
)

export const selectRaisedHands = createShallowEqualSelector(
    (state) => selectLiveModeState(state)?.raisedHands ?? EMPTY_ARRAY,
    (raisedHands) => raisedHands
)
